/**
 * Regression for plot series.
 *
 * Two surfaces:
 *   - `Regression` — least-squares curve fits (linear / polynomial /
 *     …), re-exported from `@tento-core/regression`.
 *   - `regressSeries` — Nadaraya-Watson kernel smoothing → a
 *     `DerivedChannels` (smoothed / confidence / density / trust),
 *     the data step that the regression renderers paint. Ported 1:1
 *     from the legacy `RegressionIndex.populate` + `kernel-smooth`
 *     pipeline so charts match pixel-for-pixel through the P4.6
 *     migration.
 */

import { DerivedChannels } from "../plot/derived-channels";
import { NumericSeries } from "../series/numeric-series";
import {
  kernelSmooth,
  type RegressionPoint,
} from "./kernel-smooth";

export { Regression } from "@tento-core/regression";
export {
  Kernel,
  type KernelFn,
  type KernelSmoothResult,
  kernelSmooth,
  type RegressionPoint,
} from "./kernel-smooth";
export { impute } from "../series/impute";

/**
 * Regression configuration. Mirrors the legacy `RegressionAttributes`
 * carried on a `seriesType: "regression"` data-series row.
 */
export type RegressionParams = Readonly<{
  /** Kernel window radius (x-domain units). Default 10. */
  convHalfWidth?: Option<number>;
  /** Split into separate curves at gaps wider than this. Default = halfWidth. */
  maxGap?: Option<number>;
  /** Minimum points per segment to attempt a curve. Default 3. */
  minPoints?: Option<number>;
  /** Minimum spatial coverage per segment. Default 0.5. */
  minCoverage?: Option<number>;
  /** Confidence factor (default 1.96 ≈ 95% CI). */
  confFactor?: Option<number>;
}>;

/**
 * Kernel-smooth a scalar series into a `DerivedChannels`. Segments at
 * `maxGap`, gates each segment by `minPoints` + `minCoverage`, runs the
 * Nadaraya-Watson estimator per segment, and clips each curve to its
 * segment ± `halfWidth/4`. Returns `DerivedChannels.empty()` when there
 * is nothing to smooth.
 */
export function regressSeries(
  series: NumericSeries<number>,
  params: RegressionParams = {},
): DerivedChannels {
  const points: [number, number][] = [];
  for (const [x, y] of series.points()) {
    if (typeof y === "number" && Number.isFinite(y)) points.push([x, y]);
  }
  if (points.length < 2) return DerivedChannels.empty();

  const halfWidth = params.convHalfWidth ?? 10;
  const maxGap = params.maxGap ?? halfWidth;
  const minPoints = params.minPoints ?? 3;
  const minCoverage = params.minCoverage ?? 0.5;
  const confFactor = params.confFactor ?? 1.96;

  // Split into contiguous segments at gaps > maxGap. Each segment gets
  // its own regression — no curve spans a large gap. Keeping the curves
  // per-segment (not just concatenated) lets the renderers break the line
  // at real gaps instead of drawing a misleading bridge across them.
  const segments = segmentByGap(points, maxGap);
  const tailLimit = halfWidth / 4;
  const cadence = computeCadence(segments, maxGap);
  const segmentCurves: RegressionPoint[][] = [];
  for (const seg of segments) {
    if (seg.length < minPoints) continue;
    if (!hasMinCoverage(seg, cadence, minCoverage)) continue;

    const result = kernelSmooth(seg, halfWidth, { confFactor });
    const segMin = seg[0]![0];
    const segMax = seg[seg.length - 1]![0];
    const clipped = result.curve.filter(
      (p) => p.x >= segMin - tailLimit && p.x <= segMax + tailLimit,
    );
    if (clipped.length > 0) segmentCurves.push(clipped);
  }

  const curve = segmentCurves.flat();
  if (curve.length === 0) return DerivedChannels.empty();

  return Object.freeze({
    smoothed: NumericSeries.fromPoints(curve.map((p) => [p.x, p.y] as const)),
    confidence: NumericSeries.fromPoints(curve.map((p) => [p.x, p.confidence] as const)),
    density: NumericSeries.fromPoints(curve.map((p) => [p.x, p.density] as const)),
    trust: NumericSeries.fromPoints(curve.map((p) => [p.x, p.trust] as const)),
    // Only declare segments when the curve actually breaks (>1 segment);
    // a single continuous curve leaves this null so renderers take the
    // simple single-path route.
    segments: segmentCurves.length > 1
      ? segmentCurves.map((seg) =>
        NumericSeries.fromPoints(seg.map((p) => [p.x, p.y] as const))
      )
      : null,
    buckets: null,
  });
}

// ── Segmentation helpers (ported 1:1 from RegressionIndex) ──────────

/** Split sorted points into contiguous segments at gaps > maxGap. */
function segmentByGap(
  points: readonly [number, number][],
  maxGap: number,
): [number, number][][] {
  const segments: [number, number][][] = [];
  let current: [number, number][] = [];
  for (const p of points) {
    if (current.length > 0 && p[0] - current[current.length - 1]![0] > maxGap) {
      segments.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

/**
 * Expected cadence (spacing between consecutive points): the median of
 * within-segment gaps. Cross-segment gaps are excluded (they're > maxGap
 * by construction and don't represent the series's natural spacing).
 */
function computeCadence(
  segments: readonly [number, number][][],
  fallback: number,
): number {
  const gaps: number[] = [];
  for (const seg of segments) {
    for (let i = 1; i < seg.length; i++) {
      gaps.push(seg[i]![0] - seg[i - 1]![0]);
    }
  }
  if (gaps.length === 0) return fallback;
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)] || fallback;
}

/**
 * Coverage = points / expected-positions-at-cadence. Cadence is the
 * series's expected spacing (computed from all within-segment gaps).
 */
function hasMinCoverage(
  points: readonly [number, number][],
  cadence: number,
  minCoverage: number,
): boolean {
  if (points.length < 2) return false;
  const span = points[points.length - 1]![0] - points[0]![0];
  if (span <= 0 || cadence <= 0) return true;

  const expectedPositions = Math.floor(span / cadence) + 1;
  const coverage = points.length / expectedPositions;
  return coverage >= minCoverage;
}
